import { useState, useCallback } from "react";
import { once } from "@/utilities/lodash";
import { useNavigate, Navigate } from "react-router-dom";
import { routesMap } from "@/components/routes/routes";
import { createHandler } from "@/utilities/utils";
import { MVAS } from "@/utilities/constants";
import useRelationships from "@/components/hooks/use-relationships/use-relationships";
import OpticksSpinner from "@/components/ui/OpticksSpinner/OpticksSpinner";
import OpticksBackground from "@/components/ui/OpticksBackground/OpticksBackground";
import ChevronRightIcon from "@/components/ui/OpticksIcons/ChevronRightIcon";
import type { Relationship } from "@/types/model/Roles";
import useBaseRoute from "@/components/hooks/use-base-route/use-base-route";
import useAccountFeatures from "@/components/hooks/use-account-features/use-account-features";
import { useAuthStore } from "@/zustand/authStore";
import { useAnalyticsStore } from "@/zustand/analyticsStore";
import storageService from "@/services/middleware/storageService";
import { FT_NAME } from "@/components/routes/feature-access/featureAccessResolver";
import "./RelationshipSelector.css";
import OpticksButton from "@/components/ui/OpticksButton/OpticksButton";

const RelationshipSelector = () => {
  const navigate = useNavigate();
  const [redirect, setRedirect] = useState(false);
  const { availableActiveRelationships, changeActiveRelation } =
    useRelationships();
  const { isFeatureVisible } = useAccountFeatures();
  const { setLogout } = useAuthStore((state) => state);
  const { setDimensions } = useAnalyticsStore((state) => state);

  const { baseRoute } = useBaseRoute({
    isOverviewVisible: isFeatureVisible(FT_NAME.OVERVIEW) || false,
  });

  const handleRelationshipSelection = useCallback(
    (relationship: Relationship) => {
      if (
        relationship.active === false &&
        relationship.planMode === "appsumo"
      ) {
        setLogout();
        navigate(routesMap.licenseDeactivated.path);
      } else if (relationship.active === false) {
        if ("paymentUrl" in relationship && relationship.paymentUrl !== null) {
          setLogout();
          navigate(routesMap.accountInactive.path);
        } else {
          setLogout();
          navigate(routesMap.accountMissing.path);
        }
      } else {
        changeActiveRelation(relationship.id as number)
          ?.then(() => {
            storageService.setItem(
              `${MVAS}`,
              JSON.stringify(relationship.vertical === MVAS)
            );
            setDimensions(
              relationship?.vertical === MVAS ? ["campaign"] : ["trafficType"]
            );
            setRedirect(true);
          })
          .catch((error) => {
            const redirectRoute: string =
              error.status === "VALIDATION_REQUIRED"
                ? routesMap.auth.emailVerificationPending.pathWithSearch
                : routesMap.index.path;

            setLogout();
            navigate(redirectRoute);
          });
      }
    },
    []
  );

  const handleRelationshipSelectionOnce = useCallback(
    once(handleRelationshipSelection),
    []
  );

  // if there is only one
  const firstRelationship = availableActiveRelationships?.[0];
  if (availableActiveRelationships?.length === 1 && firstRelationship) {
    handleRelationshipSelectionOnce(firstRelationship);
  }

  if (redirect) {
    return <Navigate to={baseRoute} />;
  }

  return (
    <OpticksBackground>
      {availableActiveRelationships?.length <= 1 ? (
        <OpticksSpinner variation="blue" />
      ) : (
        <div className="relationship-selector">
          <h3>Log In</h3>
          <div className="buttons-wrapper">
            {availableActiveRelationships?.map((relationship) => (
              <OpticksButton
                key={relationship.id}
                variation="secondary"
                onClick={createHandler(handleRelationshipSelectionOnce, [
                  relationship,
                ])}
                label={relationship.companyName.toUpperCase()}
                iconTrailing={
                  <ChevronRightIcon size={24} className="icon-white" />
                }
              />
            ))}
          </div>
        </div>
      )}
    </OpticksBackground>
  );
};

export default RelationshipSelector;
